'use client';

import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';

export default function LoginForm() {
  const searchParams = useSearchParams() ?? new URLSearchParams();
  const returnUrl = searchParams.get('url') || '/dongniai/';

  const [account, setAccount] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);

  // ★ [v6.23 P0 修复 2026-07-10] 改用 form submit 替代 fetch POST
  //
  // 根因（v6.22 隐藏 P0 #② 的真实升级版）：
  //   fetch('/index/user/login.html') 的 response 在浏览器里 Set-Cookie 没 apply，
  //   即使 response code=1 "登录成功" 也存不到 cookie store。
  //   后果：/api/my-courses 等下游 API 拿不到 uid/token，sidebar 显示「暂无课程」。
  //
  // 真实失败原因（curl 完整对比验证）：
  //   1. fetch same-origin POST response 在 Next.js basePath + trailingSlash:true 下
  //      实际触发 nginx 308 redirect 到 /index/user/login.html/（带尾 slash），
  //      fetch default redirect:'follow' 跟随 → trailingSlash 路径走 nginx 全局 .php
  //      → PHP 响应无 Set-Cookie → 浏览器 cookie store 什么都没收到。
  //   2. 即使 first response 没 redirect，fetch POST 的 Set-Cookie 因 SameSite/credentials 组合
  //      也没 apply（curl 完整测试：cookie 真的发了，但浏览器侧 fetch 不收）。
  //
  // 修复：
  //   用传统 form POST + method=POST + 浏览器 native navigation (top-level) →
  //   Set-Cookie 100% apply 到 cookie store, PHP 返回 200 + Set-Cookie 后直接跳转 returnUrl。
  //
  // 优点：
  //   - 不依赖 fetch/credentials 配对的微妙行为
  //   - PHP 返回后浏览器 automatic navigate 到 url 字段（FastAdmin 默认行为）
  //   - form 表单提交是 28 年 web 老标准，0 个 fetch 相关的 runtime edge case
  //
  // 注：
  //   - 已经在测试环境 curl 完整模拟验证流程：完整登录 + cookies 设到 .xiaoquanwk.com，
  //     /api/my-courses 返回 code:1 + 真实课程数据（小学数学 6 年级 整数与小数运算复习...）
  //   - 防御性保留 fetch GET 拿 CSRF token 的路径不变（GET 不带 trailing slash，
  //     nginx location = /index/user/login.html 命中返回登录表单，正面路径）

  useEffect(() => {
    // 用 fetch GET 拿 token — 这条路径已验证 OK（200 + 登录表单 HTML + token field）
    fetch('/index/user/login.html', { credentials: 'include' })
      .then((r) => r.text())
      .then((html) => {
        const m = html.match(/value="([a-f0-9]{30,})"/);
        if (m) setToken(m[1]);
      })
      .catch((e) => setError('Failed to load login page: ' + e.message));
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreed) {
      setError('请先勾选同意《用户协议》和《隐私政策》');
      return;
    }
    if (!token) {
      setError('登录页面 token 还没加载完，请稍后再试');
      return;
    }
    setLoading(true);
    setError(null);

    // 用 form submit 替代 fetch POST —— top-level navigation 自动 apply Set-Cookie
    // 不要使用 fetch POST：fetch response Set-Cookie 在 Next.js basePath + trailingSlash
    // 下被 nginx 308 串掉，cookie 不进浏览器 store（v6.22 隐藏 P0 #② 的副作用）
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/index/user/login.html';
    form.style.display = 'none';

    const fields: Record<string, string> = {
      account,
      password,
      __token__: token,
      agree: '1',
      // FastAdmin login 成功后跳转 url 字段（如果不传会跳 /index/user/index.html）
      // 显式传 returnUrl 让登录后回到 dongniai 首页
      url: returnUrl,
    };

    for (const [name, value] of Object.entries(fields)) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    }

    document.body.appendChild(form);
    form.submit();
    // 不需要 cleanup，浏览器会卸载整个页面
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
        <h1 className="text-2xl font-bold text-center mb-2">懂你 AI 课堂</h1>
        <p className="text-center text-gray-500 mb-6">登录开始学习</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">账号</label>
            <input
              type="text"
              value={account}
              onChange={(e) => setAccount(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="手机号"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">密码</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <label className="flex items-start gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-1"
            />
            <span>
              我已阅读并同意
              <a href="/agreement.html" className="text-blue-600 mx-1">《用户协议》</a>
              和
              <a href="/privacy.html" className="text-blue-600 mx-1">《隐私政策》</a>
            </span>
          </label>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !token}
            className="w-full bg-blue-600 text-white py-2.5 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {loading ? '登录中...' : '登录'}
          </button>
        </form>

        <p className="text-center text-sm text-gray-500 mt-4">
          还没有账号？
          {/* 【v6.97.85+11 P0 修复 2026-09-02 老板报】按 v6.97.83+5 模板, 注册链接绝对路径, 防 Nginx @fastadmin 兜底重写 */}
          <a href={typeof window !== 'undefined' ? window.location.origin + '/index/user/register.html' : '/index/user/register.html'} className="text-blue-600 ml-1">立即注册</a>
        </p>
      </div>
    </div>
  );
}
